/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react';

import { useField } from 'react-final-form';

import { MultipleFileInput as Input } from '@vk-hr-tek/ui/input';

import { ValidateType, composeValidators } from '../validation';

/**
 * Интерфейс пропсов компонента MultipleFileInput
 * @typedef {Object} MultipleFileInputProps
 * @property {string} label - Метка поля ввода
 * @property {React.ReactNode} [tooltip] - Всплывающая подсказка
 * @property {boolean} [required=false] - Обязательность заполнения поля
 * @property {boolean} [disabled=false] - Состояние отключения поля
 * @property {string} name - Имя поля ввода, используемое в форме
 * @property {boolean} [withAuth] Флаг авторизации
 * @property {ValidateType[]} [validate] - Массив валидаторов для поля
 * @property {number} [maxLength] - Максимальное кол-во документов, доступное для подкрепления
 * @property {(file: File) => Promise<{ file_id: string; expired_at: string }>} [cache] - Функция для кэширования файла
 * @property {boolean} [validationWithoutTouch=false] - Показывать ошибку без взаимодействия с полем.
 * @property {boolean} [validationWhenDisabled=false] - Показывать ошибку если поле задизейблено.
 */
interface MultipleFileInputProps {
  label: string;
  tooltip?: React.ReactNode;
  required?: boolean;
  disabled?: boolean;
  name: string;
  validate?: ValidateType[];
  description?: string;
  dropzoneText?: string;
  withAuth?: boolean;
  cache?: (file: File) => Promise<{ file_id: string; expired_at: string }>;
  maxLength?: number;
  validationWithoutTouch?: boolean;
  validationWhenDisabled?: boolean;
}

/**
 * Компонент для ввода нескольких файлов с возможностью валидации и кэширования
 * @param {MultipleFileInputProps} props - Пропсы компонента
 * @returns {React.ReactElement} Элемент React
 */
export const MultipleFileInput = ({
  label,
  tooltip,
  required,
  name,
  validate,
  disabled = false,
  description,
  dropzoneText,
  withAuth,
  cache,
  maxLength,
  validationWithoutTouch = false,
  validationWhenDisabled = false,
}: MultipleFileInputProps) => {
  const validators = composeValidators(validate || []);

  const { input, meta } = useField(name, {
    validate: validators,
  });

  const { onChange, onBlur } = input;

  /**
   * Флаг для отображения ошибки в зависимости от состояния disabled
   * @type {boolean}
   */
  const errorDisabled = disabled || !validationWhenDisabled;

  /**
   * Флаг для отображения ошибки
   * @type {boolean}
   */
  const renderError =
    !errorDisabled && validationWithoutTouch
      ? !!input.value && !!meta.error
      : meta.touched && !!meta.error;

  return (
    <Input
      {...input}
      value={input.value}
      label={label}
      disabled={disabled}
      required={required}
      tooltip={tooltip}
      onChange={onChange}
      onBlur={onBlur}
      cache={cache}
      validate={validators}
      error={renderError}
      helperText={renderError && meta.error}
      description={description}
      dropzoneText={dropzoneText}
      maxLength={maxLength}
      withAuth={withAuth}
    />
  );
};
